import React, { useState, type JSX } from "react";
import {
  Box,
  Stack,
  Theme,
  Typography,
  useTheme,
  IconButton,
  Skeleton,
} from "@mui/material";
import { createContext, createUseContext } from "../boundary/context";
import { usePeriodTabTranslation } from "hooks/formatters";
import {
  Period,
  minimumPeriod,
  usePeriodIndex,
  useSetPeriodIndex,
} from "hooks/periodIndex";
import { usePeriods } from "hooks/search";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useHotkeys } from "react-hotkeys-hook";
import { doLoaded } from "models/loaded";
import { hotkeys } from "hooks/hotkeys";
import { SearchPeriod } from "src/store/api/generatedApi";
import { useTenantProductionPlan } from "hooks/useTenantProductionPlan";
import { TabHeader } from "./tabHeader";
import { DeletePeriodConfirmation, PeriodMetadata } from "./actions";
import { HEADER_HEIGHT } from "src/dashboard/components/ExpandSideMenuButton";
import { SystemStyleObject } from "@mui/system";
import { useIsMobile } from "hooks/useIsMobile";

export const blankPeriod: SearchPeriod = {
  production_plan: {
    product_group_items: [],
    steel_grade_items: null,
  },
  obtainable_bundles: [],
  target_inventory: [],
  target_baskets: [],
  material_exclusivity: [],
  name: null,
  start_timestamp: null,
  end_timestamp: null,
  electrical_energy_price: 0,
  is_deployable: false,
  suppress_mix_material_exclusivity_constraints: false,
  suppress_min_feasible_mass_constraints: false,
  optimisation_objective_weighting: 1.0,
};

interface Props {
  trailingComponent?: JSX.Element;
}

const selectedStyle = (theme: Theme): SystemStyleObject<Theme> => ({
  backgroundColor: theme.palette.background.default,
  borderColor: theme.palette.common.black,
  borderBottomColor: theme.palette.common.white,
  cursor: "default",
  borderTopLeftRadius: 4,
  borderTopRightRadius: 4,
});

const getStyle = (
  isSelected: boolean,
  theme: Theme
): SystemStyleObject<Theme>[] => {
  const baseStyle = {
    px: 2,
    py: 1,
    borderStyle: "solid",
    borderWidth: "1px",
    marginBottom: "-1px",
    borderColor: "transparent",
    cursor: "pointer",
  } as const;
  if (isSelected) {
    return [baseStyle, selectedStyle(theme)];
  } else {
    return [baseStyle];
  }
};

const Context = createContext<Period>();

export const usePeriodTab = createUseContext(Context, "period_tab");

export const PeriodTabs = ({
  children,
  trailingComponent,
}: React.PropsWithChildren<Props>) => {
  const theme = useTheme();
  const period = usePeriodIndex();
  const setPeriod = useSetPeriodIndex();
  const periodTabTranslation = usePeriodTabTranslation();
  const [searchPeriods, setSearchPeriods] = usePeriods();
  const tenantProductionPlan = useTenantProductionPlan();
  const isMobile = useIsMobile();

  useHotkeys(hotkeys.nextPeriod, () => {
    doLoaded(searchPeriods, (periods) => {
      setPeriod(period < periods.length ? ((period + 1) as Period) : period);
    });
  });

  useHotkeys(
    hotkeys.previousPeriod,
    () => {
      setPeriod(period === 1 ? (1 as Period) : ((period - 1) as Period));
    },
    { splitKey: "!" }
  );

  const addPeriod = (newPeriodIndex: Period) => {
    setSearchPeriods((periods) => [
      ...periods,
      { ...blankPeriod, production_plan: tenantProductionPlan },
    ]);
    setPeriod(newPeriodIndex);
  };

  const editPeriod = (index: number) => (metadata: PeriodMetadata) => {
    setSearchPeriods((searchPeriods) => {
      const existing = searchPeriods[index];
      if (!existing) return searchPeriods;
      return [
        ...searchPeriods.slice(0, index),
        { ...existing, ...metadata },
        ...searchPeriods.slice(index + 1),
      ];
    });
  };

  const [periodToDelete, setPeriodToDelete] = useState<Period | null>(null);

  const periodTabsContent =
    searchPeriods.status === "success" ? (
      <>
        {searchPeriods.data.map(
          ({ name, start_timestamp, end_timestamp }, index) => {
            const isSelected = Number(period) === index + minimumPeriod;
            return (
              <Box
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={index}
                sx={getStyle(isSelected, theme)}
                onClick={() => {
                  setPeriod((index + minimumPeriod) as Period);
                }}
              >
                <TabHeader
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={`${index}`}
                  selected={isSelected}
                  index={(index + minimumPeriod) as Period}
                  metadata={{
                    name: name ?? null,
                    start_timestamp: start_timestamp ?? null,
                    end_timestamp: end_timestamp ?? null,
                  }}
                  setMetadata={editPeriod(index)}
                  onDelete={() => {
                    setPeriodToDelete((index + minimumPeriod) as Period);
                  }}
                />
              </Box>
            );
          }
        )}
        <IconButton
          size="small"
          color="primary"
          onClick={() => {
            addPeriod((searchPeriods.data.length + 1) as Period);
          }}
          sx={{ px: 2, flexShrink: 0 }}
        >
          <AddCircleOutlineIcon fontSize="inherit" />
        </IconButton>
      </>
    ) : (
      <Box sx={getStyle(true, theme)}>
        <Typography
          variant="button"
          sx={{
            maxWidth: "200px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            textWrap: "nowrap",
            display: "block",
          }}
        >
          <Skeleton sx={{ width: 60 }} />
        </Typography>
      </Box>
    );

  return (
    <>
      <Stack
        sx={{
          flexDirection: "column",
          height: "100%",
          width: "100%",
          minHeight: 0,
        }}
      >
        {isMobile ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              backgroundColor: theme.palette.grey[50],
              borderBottomColor: theme.palette.common.black,
              borderBottomStyle: "solid",
              borderBottomWidth: "1px",
              flexShrink: 0,
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>{trailingComponent ?? null}</Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-end",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
                px: 2,
              }}
            >
              {periodTabsContent}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              justifyContent: "space-between",
              borderBottomColor: theme.palette.common.black,
              borderBottomStyle: "solid",
              borderBottomWidth: "1px",
              backgroundColor: theme.palette.grey[50],
              paddingX: 2,
              height: HEADER_HEIGHT,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex" }}>{periodTabsContent}</Box>
            <Stack height="100%" justifyContent="center">
              {trailingComponent ?? null}
            </Stack>
          </Box>
        )}

        {children}
      </Stack>

      {periodToDelete !== null &&
      searchPeriods.status === "success" &&
      searchPeriods.data[periodToDelete - 1] ? (
        <DeletePeriodConfirmation
          periodToDelete={periodToDelete}
          setPeriodToDelete={setPeriodToDelete}
          name={
            searchPeriods.data[periodToDelete - 1]?.name ??
            periodTabTranslation(periodToDelete)
          }
        />
      ) : null}
    </>
  );
};
